import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier } from 'firebase/auth';
import { auth } from '../constants/firebaseModular';
import { scrubSafeAuthCode } from '../utils/mfa/sentryAuthCode';
import { browserReportsOnline, isNetworkError } from '../utils/mfa/networkError';
import { flushOfflineFailures, noteOfflineFailure } from '../utils/mfa/offlineFailureReport';
import { dedupeSafeMessage } from '../utils/mfa/sentryDedupe';

/**
 * The SMS login challenge — CU-86ajwpnek §2.
 *
 * Ported from the admin console's `useMfaChallenge` (CU-86ajva3wz). Nearly a
 * drop-in: the ceremony and the error vocabulary are identical, only the copy
 * and the "start over" affordance differ.
 *
 * ⚠️ THIS HOOK MINTS NOTHING. Unlike enrollment there is no C3 checkpoint and no
 * authorization: the resulting session is MFA-satisfied because Identity
 * Platform says so, not because we asserted it. `resolveSignIn` returns a real
 * `UserCredential`, so `onAuthStateChanged` in AuthContext picks the session up
 * on its own — there is no commit step to add.
 *
 * ⚠️ THE RESOLVER IS A LIVE OBJECT carrying the pending sign-in. It CANNOT be
 * serialized — never put it in TanStack Router state, `sessionStorage`,
 * `localStorage`, or a URL. Local component state only. A reload loses it and
 * drops the patient back to sign-in, which is intended: the password still
 * works as the durable root, so they are never trapped.
 *
 * ⚠️ WEB IS A THIRD API SHAPE. Mobile uses
 * `verifyPhoneNumberWithMultiFactorInfo(hint, session)` — two positional args.
 * THAT METHOD DOES NOT EXIST IN THE JS SDK. Web takes an options object plus a
 * `RecaptchaVerifier`, because a browser has no silent APNs / Play Integrity.
 * Signature verified 2026-08-07 against firebase 11.10.0's OWN
 * `auth-public.d.ts` (`PhoneAuthProvider.verifyPhoneNumber(phoneOptions:
 * PhoneInfoOptions | string, applicationVerifier?: ApplicationVerifier)`, with
 * `PhoneMultiFactorSignInInfoOptions = { multiFactorHint?, multiFactorUid?,
 * session }`) — NOT from documentation. This project has shipped a wrong
 * signature from docs twice.
 *
 * ── PHI DISCIPLINE (M3) ───────────────────────────────────────────────────
 * ⚠️ NEVER send the phone number, the OTP, or the verification id to Sentry.
 * The masked number comes from IdP and is displayed verbatim — never unmasked,
 * reconstructed, or echoed back.
 */

export type MfaChallengeStep = 'sending' | 'code' | 'done';

export type MfaChallengeError =
  | { kind: 'bad-code' }
  | { kind: 'session-expired' }
  | { kind: 'too-many-requests' }
  | { kind: 'send-failed' }
  | { kind: 'resend-cap-reached' }
  /**
   * The request never reached the server (CU-86akbg4qm) — the one failure a
   * patient can always fix themselves, so the copy says "reconnect" and does
   * NOT route to support.
   *
   * ⚠️ MEASURED 2026-09-24: before this kind existed, an offline SEND showed
   * `send-failed` ("try again in a moment") and an offline RESOLVE showed
   * `generic` (contact support). Both legs throw `auth/network-request-failed`.
   *
   * Set only from `isNetworkError` — a request that ACTUALLY failed, never a
   * connectivity prediction.
   */
  | { kind: 'offline' }
  | { kind: 'generic' };

/**
 * ⚠️ Structural types, declared locally on purpose: the web SDK's own
 * `MultiFactorResolver` / `MultiFactorInfo` are not exported in a shape that can
 * be satisfied by a test fake, and depending on them here would force every test
 * to construct a real IdP object.
 */
interface PhoneHint {
  factorId: string;
  uid?: string;
  phoneNumber?: string;
}

export interface Resolver {
  hints: PhoneHint[];
  session: unknown;
  resolveSignIn: (_assertion: unknown) => Promise<unknown>;
}

export interface UseMfaChallenge {
  step: MfaChallengeStep;
  busy: boolean;
  error: MfaChallengeError | null;
  /** IdP's OWN mask, displayed verbatim. Never unmask or reconstruct it (M3). */
  maskedPhone: string | null;
  sendCode: () => Promise<boolean>;
  submitCode: (_code: string) => Promise<boolean>;
  clearError: () => void;
  resendCooldownSecs: number;
  resendCapReached: boolean;
}

export interface MfaChallengeDeps {
  makeVerifier?: (_containerId: string) => { clear?: () => void };
  verifyPhoneNumber?: (_options: unknown, _verifier: unknown) => Promise<string>;
  buildAssertion?: (_verificationId: string, _code: string) => unknown;
  now?: () => number;
}

const RESEND_COOLDOWNS_MS = [30_000, 60_000, 120_000];
const RESEND_CAP = 5;

/**
 * @param resolver `null` leaves the hook INERT, so the form can mount before a
 *   resolver exists without dispatching anything.
 */
export function useMfaChallenge(
  resolver: Resolver | null,
  containerId: string,
  deps: MfaChallengeDeps = {},
  /**
   * The email typed at the password step. Recorded as a Sentry tag so a
   * challenge-leg failure can be tied to an attempt (CU-86akbcqv8).
   *
   * ⚠️ A CLAIM, NEVER A VERIFIED IDENTITY. The patient has typed a correct
   * password but has NOT proven the second factor, and an attacker walking a
   * list types addresses belonging to real patients who did nothing. Nothing
   * downstream — support tooling, access review, audit trail — may read this
   * as evidence of who was signing in.
   *
   * ⚠️ NEVER `Sentry.setUser`. Scope identity would attach it to every
   * subsequent event and attribute a security event to whoever was typed.
   *
   * ⚠️ WHY NOT THE UID: the resolver exposes only `hints`, `session` and
   * `resolveSignIn` — there is NO account uid on it. Firebase withholds account
   * identity until the factor is proven, which is the same boundary that makes
   * this leg pre-auth.
   *
   * ⚠️ WHY NOT TRACE CORRELATION: measured 2026-09-03 — the trace spans a whole
   * session, so it cannot separate one sign-in attempt from the next. A probe
   * found 2 of 3 attempts by trace versus 3 of 3 by this tag. This is not the
   * better correlator, it is the only one.
   *
   * ⚠️ NOT in `deps`: that parameter is a TEST SEAM, and production data must
   * not travel through it.
   */
  claimedEmail?: string,
): UseMfaChallenge {
  // Starts at 'sending': the OTP auto-dispatches. The patient has already
  // proven their password, so making them click "send me a code" is a step
  // with no decision in it.
  const [step, setStep] = useState<MfaChallengeStep>('sending');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MfaChallengeError | null>(null);

  const verificationIdRef = useRef<string | null>(null);
  /** When the OTP was dispatched, so a failure can report REAL elapsed time. */
  const sentAtRef = useRef<number | null>(null);
  const sendCountRef = useRef(0);
  const firstSentAtRef = useRef<number | null>(null);
  /** The cap signal is sent ONCE per challenge — the button stays pressable at the cap. */
  const capReportedRef = useRef(false);
  const verifierRef = useRef<{ clear?: () => void } | null>(null);
  const [resendAllowedAt, setResendAllowedAt] = useState(0);
  const [tick, setTick] = useState(0);

  // Memoized rather than rebuilt inline — see the note in useSmsEnrollment.
  const nowRef = useRef(deps.now);
  nowRef.current = deps.now;
  const now = useCallback(() => (nowRef.current ?? Date.now)(), []);

  // Stops once the cooldown elapses (CU-86ajtgauu): `resendAllowedAt` never
  // returns to 0, so an interval that only stopped on unmount re-rendered this
  // form every second for as long as it stayed open.
  useEffect(() => {
    if (resendAllowedAt === 0 || now() >= resendAllowedAt) return undefined;
    const id = setInterval(() => {
      setTick((t) => t + 1);
      if (now() >= resendAllowedAt) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [resendAllowedAt, now]);

  /**
   * Tear down the current verifier AND empty its container.
   *
   * ⚠️ `clear()` ALONE IS NOT ENOUGH, AND NEITHER IS EMPTYING THE CONTAINER.
   * Firebase renders an INVISIBLE verifier straight into our container (a
   * VISIBLE one gets a fresh `guaranteedEmpty` child instead), and `grecaptcha`
   * marks that ELEMENT as occupied — a marker inside Google's script, not in
   * DOM we can clear. Only a genuinely new element works.
   *
   * ⚠️ MEASURED ON STAGING 2026-08-07 (Safari 26.6, MIDEO-UNIFIED-8W) on the
   * enrollment path. The challenge path is MORE exposed, not less: resending a
   * code at login is routine. Same fix, same reasoning — see `useSmsEnrollment`.
   */
  const teardownVerifier = useCallback(() => {
    try {
      verifierRef.current?.clear?.();
    } catch {
      // Already destroyed, or a failed render. Either way the node below still
      // needs removing.
    }
    verifierRef.current = null;
    if (typeof document === 'undefined') return;
    const stale = document.getElementById(containerId);
    if (!stale?.parentNode) return;

    // ⚠️ DO NOT REMOVE THE NODE SYNCHRONOUSLY. reCAPTCHA schedules a 500ms
    // delayed hide animation that reaches back into its badge element:
    //
    //     p[48](2, function(){ K[44](15, this.g, "top", "-10000px") }, 500, …)
    //
    // Removing the container first makes `this.g` null when that timer fires,
    // and Google's own callback throws `Cannot read properties of null
    // (reading 'style')` — unhandled, because it is inside their code.
    // MEASURED on staging 2026-08-07 (MIDEO-UNIFIED-8Y, Chrome 151) when a
    // completed challenge unmounted the form.
    //
    // Harmless in itself — a fire-and-forget animation on a widget nobody can
    // see — but post-cutover it would fire on every enrolled patient's every
    // login and bury real faults in noise.
    //
    // So: strip the id and hide it NOW (a fresh container can be minted
    // immediately, which is what the id is for), and delete the node only after
    // the animation window has passed.
    stale.removeAttribute('id');
    stale.style.display = 'none';
    const doomed = stale;
    setTimeout(() => doomed.remove(), 1000);
  }, [containerId]);

  /**
   * Create a fresh container inside the host's stable wrapper. See the twin in
   * `useSmsEnrollment` for the full reasoning — in short, React must not own
   * the container, because a resend needs a genuinely NEW element and React
   * reconciliation would fight a swap.
   */
  const prepareContainer = useCallback((): string => {
    teardownVerifier();
    const host = document.getElementById(`${containerId}-host`);
    if (!host) return containerId;
    const fresh = document.createElement('div');
    fresh.id = containerId;
    host.appendChild(fresh);
    return containerId;
  }, [containerId, teardownVerifier]);

  // ⚠️ NOT IN THE CONSOLE, which leaks the widget and its IdP token on unmount.
  useEffect(() => {
    return () => {
      teardownVerifier();
    };
  }, [teardownVerifier]);

  const msRemaining = Math.max(0, resendAllowedAt - now());
  void tick;
  const resendCooldownSecs = Math.ceil(msRemaining / 1000);
  const resendCapReached = sendCountRef.current >= RESEND_CAP;

  // TOTP hints are deliberately unhandled — TOTP was dropped from the factor
  // model on purpose, so a non-phone hint must not be silently treated as one.
  const phoneHint = useMemo(() => resolver?.hints?.find((h) => h?.factorId === 'phone') ?? null, [resolver]);
  const maskedPhone = phoneHint?.phoneNumber ?? null;

  const clearError = useCallback(() => setError(null), []);

  /**
   * Classify a failure. `stepTag` decides the fallback, so the same function
   * yields `send-failed` on the send path and `generic` on the resolve path.
   * Reused rather than re-derived: two divergent classifiers on one ceremony is
   * how one of them drifts.
   */
  const classify = useCallback((err: unknown, stepTag: 'send' | 'resolve', capture = true): MfaChallengeError => {
    const code = (err as { code?: string })?.code ?? '';
    const message = (err as { message?: string })?.message ?? '';

    const isBadCode =
      code === 'auth/invalid-verification-code' ||
      code === 'auth/invalid-verification-id' ||
      code === 'auth/code-expired';
    const isSessionExpired =
      code === 'auth/user-token-expired' || code === 'auth/session-expired' || code === 'auth/invalid-user-token';
    const isThrottled = code === 'auth/too-many-requests';
    // ⚠️ AFTER the exact codes above (the prose half of the predicate is the
    // weaker signal) and BEFORE the `send-failed` / `generic` fallbacks — a
    // catch-all ahead of it would swallow every offline failure, which is the
    // defect this kind exists to fix (CU-86akbg4qm).
    const isOffline = isNetworkError(code, message);

    const kind: MfaChallengeError['kind'] = isBadCode
      ? 'bad-code'
      : isSessionExpired
        ? 'session-expired'
        : isThrottled
          ? 'too-many-requests'
          : isOffline
            ? 'offline'
            : stepTag === 'send'
              ? 'send-failed'
              : 'generic';

    if (capture) {
      // ⚠️ CLAIMED, not verified — see the hook's `claimedEmail` parameter doc.
      const emailTag = claimedEmail ? { claimed_email: claimedEmail.trim().toLowerCase() } : {};
      // A wrong code is ordinary user error. Everything else is worth seeing.
      if (kind === 'offline') {
        // ⚠️ NOT captured: an event captured offline is discarded by the
        // transport (measured), so it would record nothing. Held instead, and
        // reported once the network is back — see `offlineFailureReport.ts`.
        noteOfflineFailure({ feature: 'mfa-sms', step: `challenge-${stepTag}`, code, classified: true });
      } else if (kind === 'bad-code') {
        Sentry.addBreadcrumb({
          category: 'mfa-sms',
          level: 'warning',
          message: 'challenge: verification code rejected',
          // ⚠️ SCRUB-SAFE (CU-86akbcqv8). A raw `auth/…` value redacts the
          // ENTIRE breadcrumb — message line included — so this branch was
          // recording nothing. Measured, not inferred; see `sentryAuthCode.ts`.
          data: { code: scrubSafeAuthCode(code) },
        });
      } else {
        Sentry.captureException(err, {
          // ⚠️ THE CODE IS A TAG (CU-86akbcqv8). Tags are exempt from value
          // scrubbing; `extra` is not, and every Firebase code contains
          // `auth/`. It is also indexed and searchable in Discover.
          // ⚠️ Tags cap at 200 chars: closed-set codes ONLY, never prose.
          tags: {
            feature: 'mfa-sms',
            step: `challenge-${stepTag}`,
            auth_error_code: scrubSafeAuthCode(code),
            ...emailTag,
          },
          // NEVER the phone number, the OTP or the verification id.
          //
          // ⚠️ `message` STAYS AND STAYS SCRUBBED (Shaun's ruling 2026-09-03).
          // Firebase prose contains `auth/…`, so the default scrubbers redact
          // it here — deliberately not worked around. Exempting a free-text
          // field from EVERY default keyword is the widest grant available, and
          // full messages are already visible to support elsewhere.
          extra: { message: message || '(none)' },
        });
        // Unclassified, but the browser says it has no network: this capture
        // will be discarded with the rest, so also hold it for the recovery
        // event. Telemetry only — the patient still sees the fallback copy.
        if (!browserReportsOnline()) {
          noteOfflineFailure({ feature: 'mfa-sms', step: `challenge-${stepTag}`, code, classified: false });
        }
      }
    }
    return { kind };
    // ⚠️ `claimedEmail` IS A DEPENDENCY (CU-86akbcqv8). This array was `[]` when
    // the callback closed over nothing mutable; it now reads the email, and an
    // empty array would pin the value captured on first render — recording a
    // stale address, or none, after a re-render.
  }, [claimedEmail]);

  const sendCode = useCallback(async (): Promise<boolean> => {
    if (busy) return false;
    if (!resolver || !phoneHint) return false;

    // Both throttle guards run BEFORE the verifier is built, so a blocked
    // resend costs nothing: no reCAPTCHA render, no IdP call, no SMS.
    if (sendCountRef.current >= RESEND_CAP) {
      // Reached when a patient presses resend AT the cap — someone wanted a 6th
      // code. Either SMS is not being delivered or someone is pumping messages
      // at a phone. Both deserve investigation.
      //
      // ⚠️ UNREACHABLE THROUGH THE UI until CU-86ajtgauu: the form disabled the
      // button at the cap, and the button is the only caller. The hook test and
      // the (mocked-hook) form test each passed on their own half.
      //
      // No phone number, OTP or verification id. Counts, elapsed time and — on
      // the login mount — the CLAIMED email tag, a determined field that tells
      // one patient failing repeatedly apart from someone pumping. Matches the
      // mobile app's cap event.
      if (!capReportedRef.current) {
        capReportedRef.current = true;
        Sentry.captureMessage('mfa challenge: resend cap reached', {
          level: 'warning',
          tags: {
            feature: 'mfa-sms',
            step: 'challenge-resend-cap',
            // ⚠️ CLAIMED, not verified — see the hook's `claimedEmail` parameter doc.
            ...(claimedEmail ? { claimed_email: claimedEmail.trim().toLowerCase() } : {}),
          },
          extra: {
            resendCap: RESEND_CAP,
            otpSendCount: sendCountRef.current,
            msSinceFirstSend: firstSentAtRef.current === null ? null : now() - firstSentAtRef.current,
          },
        });
      }
      setError({ kind: 'resend-cap-reached' });
      return false;
    }
    // The FIRST send is never throttled.
    if (sendCountRef.current > 0 && now() < resendAllowedAt) {
      // The button is disabled and counting down, so refuse silently rather
      // than scolding them.
      return false;
    }

    setBusy(true);
    setError(null);
    try {
      const freshContainerId = prepareContainer();
      const verifier = deps.makeVerifier
        ? deps.makeVerifier(freshContainerId)
        : new RecaptchaVerifier(auth, freshContainerId, { size: 'invisible' });
      verifierRef.current = verifier;

      const verify =
        deps.verifyPhoneNumber ??
        ((options: unknown, v: unknown) => new PhoneAuthProvider(auth).verifyPhoneNumber(options as never, v as never));

      // ⚠️ WEB SHAPE — verified against firebase 11.10.0's own types.
      const verificationId = await verify({ multiFactorHint: phoneHint, session: resolver.session }, verifier);

      verificationIdRef.current = verificationId;
      sentAtRef.current = now();
      if (firstSentAtRef.current === null) firstSentAtRef.current = now();
      sendCountRef.current += 1;

      // Armed only AFTER a send succeeds — a failed dispatch must not lock the
      // patient out of retrying it.
      const resendsMade = sendCountRef.current - 1;
      setResendAllowedAt(now() + RESEND_COOLDOWNS_MS[Math.min(resendsMade, RESEND_COOLDOWNS_MS.length - 1)]);

      // A request just reached IdP, so the network is back: report any
      // offline failures held from earlier in this page.
      flushOfflineFailures('success');

      setStep('code');
      return true;
    } catch (err) {
      setError(classify(err, 'send'));
      return false;
    } finally {
      setBusy(false);
    }
  }, [busy, claimedEmail, classify, deps, now, phoneHint, prepareContainer, resendAllowedAt, resolver]);

  const submitCode = useCallback(
    async (code: string): Promise<boolean> => {
      if (busy) return false;
      if (!resolver) return false;
      const verificationId = verificationIdRef.current;
      if (!verificationId) {
        setError({ kind: 'generic' });
        return false;
      }

      setBusy(true);
      setError(null);
      try {
        const assertion = deps.buildAssertion
          ? deps.buildAssertion(verificationId, code)
          : PhoneMultiFactorGenerator.assertion(PhoneAuthProvider.credential(verificationId, code));

        // The session becomes MFA-satisfied here. `resolveSignIn` returns a real
        // UserCredential, so AuthContext's onAuthStateChanged picks it up.
        await resolver.resolveSignIn(assertion);

        flushOfflineFailures('success');
        setStep('done');
        return true;
      } catch (err) {
        // `capture: false` — the diagnostic below is the single record for this
        // path, so classifying here too would double-count it.
        const classified = classify(err, 'resolve', false);

        // ⚠️ OFFLINE IS HELD, NOT CAPTURED (CU-86akbg4qm). The diagnostic below
        // would be discarded by the transport while offline — measured on
        // staging 2026-09-24 — so it would record nothing. The held record is
        // reported once the network is back.
        if (classified.kind === 'offline') {
          noteOfflineFailure({
            feature: 'mfa-sms',
            step: 'challenge-resolve',
            code: (err as { code?: string })?.code ?? '',
            classified: true,
          });
          setError(classified);
          return false;
        }

        // ⚠️ H5 — AN OPEN QUESTION INHERITED FROM MOBILE, NOT A PORTAL BUG.
        // `auth/session-expired` was measured at resolve time on codes entered
        // SECONDS after the SMS — 9 occurrences across 2 accounts on Android,
        // 2026-07-29. The leading hypothesis (unproven) is throttle degradation
        // rather than true expiry, and `msSinceOtpSent` is the only thing that
        // can settle it. Fires on every non-offline resolve failure,
        // deliberately.
        //
        // ⚠️ "EVERY" WAS FALSE UNTIL CU-86akbg4qm. Sentry's `Dedupe` dropped a
        // second consecutive identical message before it left the browser —
        // measured live 2026-09-24 (client report: `event_processor`, 1 error).
        // H5's signal, a `session-expired` AFTER earlier failures, was exactly
        // the event being dropped. The message now varies per occurrence and
        // the STABLE fingerprint keeps them in one issue — see `sentryDedupe`.
        // (The fingerprint started a new issue; MIDEO-UNIFIED-8X holds the
        // history before it.)
        //
        // If this reproduces on web, that is a real finding — do NOT "fix" it
        // by loosening the code path.
        //
        // Presence BOOLEANS, never values: the verification id and the OTP are
        // credentials (M3).
        Sentry.captureMessage(dedupeSafeMessage('mfa challenge: resolve failed'), {
          level: 'error',
          fingerprint: ['mfa-sms', 'challenge-resolve-diagnostic'],
          // ⚠️ MEASURED ON STAGING 2026-09-03 (MIDEO-UNIFIED-8X). This site was
          // MISSED by the first pass of CU-86akbcqv8 — the tags went onto
          // `classify`'s capture and its breadcrumb, but not here, so a real
          // staging run produced `code: "[Filtered]"`, `message: "[Filtered]"`
          // and NO readable code anywhere on the event.
          //
          // The unit tests all drove `sendCode` → `classify`; nothing exercised
          // this path, so they passed unchanged. Covering the code path you are
          // thinking about is not the same as covering the one that ships.
          tags: {
            feature: 'mfa-sms',
            step: 'challenge-resolve-diagnostic',
            auth_error_code: scrubSafeAuthCode((err as { code?: string })?.code),
            ...(claimedEmail ? { claimed_email: claimedEmail.trim().toLowerCase() } : {}),
          },
          extra: {
            classifiedAs: classified.kind,
            // ⚠️ Kept for continuity, but EXPECT `[Filtered]` on any real
            // `auth/…` value. The readable copy is the tag above.
            code: (err as { code?: string })?.code ?? 'unknown',
            message: (err as { message?: string })?.message ?? '(none)',
            msSinceOtpSent: sentAtRef.current === null ? null : now() - sentAtRef.current,
            otpSendCount: sendCountRef.current,
            hadVerificationId: verificationIdRef.current !== null,
            hadResolverSession: resolver.session !== undefined && resolver.session !== null,
            hintCount: resolver.hints?.length ?? 0,
            codeLength: code.length,
          },
        });

        // Same telemetry-only hint as `classify`: an unclassified failure while
        // the browser reports no network is probably offline with a code we did
        // not predict, and the diagnostic above is about to be discarded.
        if (classified.kind === 'generic' && !browserReportsOnline()) {
          noteOfflineFailure({
            feature: 'mfa-sms',
            step: 'challenge-resolve',
            code: (err as { code?: string })?.code ?? '',
            classified: false,
          });
        }

        setError(classified);
        return false;
      } finally {
        setBusy(false);
      }
    },
    // `claimedEmail` — read directly by the resolve diagnostic's tags above,
    // not only through `classify` (CU-86akbcqv8).
    [busy, classify, claimedEmail, deps, now, resolver],
  );

  return {
    step,
    busy,
    error,
    maskedPhone,
    sendCode,
    submitCode,
    clearError,
    resendCooldownSecs,
    resendCapReached,
  };
}
